import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  IndianRupee, FileText, AlertTriangle, Clock, TrendingUp,
  ShoppingCart, Inbox, Bell, Plus, Upload, Loader2,
  ArrowRight, CheckCircle2, CreditCard, BarChart3, XCircle, UserCheck, Send,
  Sparkles, Zap, Lock, Building2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { usePlan } from "../context/PlanContext";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { formatIndianDate } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (a) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

export default function DashboardPage() {
  const { user, isAuthenticated, loading: authLoading, hasCompany } = useAuth();
  const { plan, isTrial, trialDaysLeft, isPaid, isFree, planData } = usePlan();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate("/auth"); return; }
    if (isAuthenticated) { fetchDashboard(); fetchPendingInvites(); fetchDocRequests(); }
  }, [isAuthenticated, authLoading]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/builder/dashboard/summary`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error("Dashboard fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const res = await axios.get(`${API}/ca/pending-invites`, { withCredentials: true });
      setPendingInvites(res.data.invites || []);
    } catch { /* silent */ }
  };

  const fetchDocRequests = async () => {
    try {
      const res = await axios.get(`${API}/ca/document-requests`, { withCredentials: true });
      const pending = (res.data.requests || []).filter(r => r.status !== "completed");
      setDocRequests(pending);
    } catch { /* silent */ }
  };

  const handleDocUpload = async (requestId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10MB"); return; }
    setUploadingFor(requestId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/ca/document-requests/${requestId}/upload`, fd, { withCredentials: true });
      toast.success("File uploaded successfully!");
      fetchDocRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  const handleInviteAction = async (mappingId, action) => {
    try {
      await axios.post(`${API}/ca/invite/${mappingId}/${action}`, {}, { withCredentials: true });
      toast.success(action === "accept" ? "CA connected successfully!" : "Invitation rejected");
      setPendingInvites(prev => prev.filter(i => i.mapping_id !== mappingId));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const d = data || {};

  const freeLockedCard = isFree && !isTrial;

  const statCards = [
    { label: "Pending Invoices", value: d.pending_invoices ?? 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-50", locked: false },
    { label: "Overdue", value: d.overdue_count ?? 0, icon: AlertTriangle, color: d.overdue_count > 0 ? "text-red-600" : "text-slate-400", bg: d.overdue_count > 0 ? "bg-red-50" : "bg-slate-50", locked: false },
    { label: "Total Receivable", value: fmt(d.total_receivable), icon: IndianRupee, color: "text-violet-600", bg: "bg-violet-50", locked: false },
    { label: "Total Invoices", value: d.total_invoices ?? 0, icon: FileText, color: "text-slate-600", bg: "bg-slate-100", locked: false },
    { label: "Sales This Month", value: fmt(d.sales_this_month), icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50", locked: freeLockedCard, lockMsg: "Unlock monthly sales insights" },
    { label: "Purchases This Month", value: fmt(d.purchases_this_month), icon: ShoppingCart, color: "text-blue-600", bg: "bg-blue-50", locked: freeLockedCard, lockMsg: "Unlock purchase insights" },
  ];

  const statusBadge = (s) => {
    const map = {
      paid: "bg-emerald-50 text-emerald-700",
      partial: "bg-amber-50 text-amber-700",
      unpaid: "bg-red-50 text-red-700",
    };
    return map[s] || "bg-slate-50 text-slate-600";
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-6 sm:py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]" data-testid="dashboard-welcome">
              Welcome back, {user?.name?.split(" ")[0] || "User"}
            </h1>
            <p className="text-[#6B7280] mt-1 text-sm">Here's your business overview for today</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {isFree && !isTrial ? (
              <Button variant="outline" onClick={() => navigate("/pricing")} className="text-sm border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="dashboard-unlock-btn">
                <Lock className="w-4 h-4 mr-1.5" /> Unlock Insights
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/upload")} className="text-sm" data-testid="dashboard-upload-btn">
                <Upload className="w-4 h-4 mr-1.5" /> Analyze
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/quotations/new")} className="text-sm" data-testid="dashboard-create-quotation-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Create Quotation
            </Button>
            <Button onClick={() => navigate("/create")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm" data-testid="dashboard-create-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Invoice
            </Button>
          </div>
        </div>

        {/* Quick Invoice Hero — for users who haven't created their first invoice yet */}
        {(data?.total_invoices ?? 0) === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-violet-50 p-5 sm:p-6"
            data-testid="quick-invoice-hero"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full mb-2">
                  <Sparkles className="w-3 h-3" /> New here
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Your First Invoice</h2>
                <p className="text-sm text-slate-600 mt-1 max-w-lg">
                  No setup needed. Fill in what you need, we'll save your details along the way.
                </p>
              </div>
              <Button
                onClick={() => navigate("/create")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-5 py-5"
                data-testid="hero-create-first-invoice-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Create First Invoice <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            {!hasCompany && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>Recommended:</span>
                <button
                  onClick={() => navigate("/company/setup")}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 hover:border-blue-300 hover:text-blue-700 transition-colors"
                  data-testid="soft-complete-profile-btn"
                >
                  <Building2 className="w-3 h-3" /> Complete business profile
                </button>
                <span className="text-slate-400">— saves time on future invoices</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Subscription Status Banner */}
        {isTrial && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-violet-600 to-blue-600 rounded-xl p-4 mb-6 text-white flex items-center justify-between gap-4"
            data-testid="dash-trial-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold">Growth Trial Active</p>
                <p className="text-xs text-white/80">{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining &middot; All premium features unlocked</p>
              </div>
            </div>
            <Button onClick={() => navigate("/pricing")} size="sm" className="bg-white text-violet-700 hover:bg-white/90 text-xs font-semibold flex-shrink-0" data-testid="dash-trial-upgrade-btn">
              <Zap className="w-3 h-3 mr-1" /> Upgrade Now
            </Button>
          </motion.div>
        )}

        {isPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-center justify-between gap-4" data-testid="dash-paid-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">{plan === 'yearly' ? 'Yearly' : 'Growth'} Plan Active</p>
                <p className="text-xs text-emerald-600">{plan === 'yearly' ? 'Annual' : 'Monthly'} subscription &middot; All features unlocked</p>
              </div>
            </div>
            <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full" data-testid="dash-paid-badge">Active</span>
          </div>
        )}

        {isFree && !isTrial && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="dash-free-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-500" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">You are on Free Plan</h3>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-2.5">
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Unlimited invoice creation</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Last 50 invoices accessible</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Basic sharing enabled</span>
                </div>
                <p className="text-xs text-slate-400">Upgrade to unlock GST Reports, Analytics, Full Insights & CA Collaboration</p>
              </div>
              <Button onClick={() => navigate("/pricing")} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold flex-shrink-0" data-testid="dash-free-upgrade-btn">
                <Sparkles className="w-3 h-3 mr-1.5" /> View Plans
              </Button>
            </div>
          </div>
        )}

        {/* Onboarding Checklist (auto-hides when complete or dismissed) */}
        <OnboardingChecklist />

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="dashboard-stats">
          {statCards.map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`bg-white rounded-xl border border-slate-100 shadow-sm p-5 relative overflow-hidden ${card.locked ? "cursor-pointer" : ""}`}
              onClick={card.locked ? () => navigate("/pricing") : undefined}
              data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {card.locked ? (
                <>
                  {/* Blurred content behind */}
                  <div className="pointer-events-none select-none" style={{ filter: "blur(6px)", opacity: 0.3 }}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center`}>
                        <card.icon className={`w-4 h-4 ${card.color}`} />
                      </div>
                      <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">{card.label}</span>
                    </div>
                    <p className="text-2xl font-bold text-[#111827]">{card.value}</p>
                  </div>
                  {/* Lock overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
                    <Lock className="w-4 h-4 text-violet-500 mb-1.5" />
                    <p className="text-[11px] font-medium text-violet-700 text-center px-3">{card.lockMsg}</p>
                    <span className="text-[10px] text-violet-500 mt-1">Used during your trial. Upgrade to continue.</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center`}>
                      <card.icon className={`w-4 h-4 ${card.color}`} />
                    </div>
                    <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">{card.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-[#111827]">{card.value}</p>
                </>
              )}
            </motion.div>
          ))}
        </div>

        {/* Pending CA Invitations */}
        {pendingInvites.length > 0 && (
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 mb-6" data-testid="pending-ca-invites">
            <div className="flex items-center gap-2 mb-3">
              <UserCheck className="w-4.5 h-4.5 text-violet-600" />
              <h3 className="text-sm font-semibold text-violet-800">CA Invitations ({pendingInvites.length})</h3>
            </div>
            <div className="space-y-2">
              {pendingInvites.map(inv => (
                <div key={inv.mapping_id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-violet-100" data-testid={`invite-${inv.mapping_id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{inv.ca_name || inv.ca_email}</p>
                    <p className="text-xs text-slate-500">Wants to manage your invoices & GST</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleInviteAction(inv.mapping_id, "accept")} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`accept-invite-${inv.mapping_id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleInviteAction(inv.mapping_id, "reject")} className="text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-invite-${inv.mapping_id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Document Requests from CA */}
        {docRequests.length > 0 && (
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-6" data-testid="pending-doc-requests">
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-4 h-4 text-orange-600" />
              <h3 className="text-sm font-semibold text-orange-800">Document Requests from Your CA ({docRequests.length})</h3>
            </div>
            <div className="space-y-2">
              {docRequests.map(req => (
                <div key={req.request_id} className="bg-white rounded-lg p-3 border border-orange-100" data-testid={`dash-doc-req-${req.request_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">{req.title}</p>
                        {req.priority && req.priority !== "normal" && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            req.priority === "urgent" ? "bg-red-50 text-red-700" :
                            req.priority === "high" ? "bg-orange-50 text-orange-700" : "bg-slate-50 text-slate-600"
                          }`}>{req.priority}</span>
                        )}
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          req.status === "submitted" ? "bg-blue-50 text-blue-700" :
                          req.status === "overdue" ? "bg-red-50 text-red-700" :
                          "bg-amber-50 text-amber-700"
                        }`}>{req.status}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        From: {req.ca_name || "Your CA"}
                        {req.due_date && ` | Due: ${formatIndianDate(req.due_date)}`}
                        {req.description && ` | ${req.description}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleDocUpload(req.request_id, e.target.files?.[0])}
                          data-testid={`upload-input-${req.request_id}`}
                        />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors cursor-pointer">
                          {uploadingFor === req.request_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Upload className="w-3 h-3" />
                          )}
                          Upload
                        </span>
                      </label>
                    </div>
                  </div>
                  {req.uploaded_count > 0 && (
                    <p className="text-[10px] text-emerald-600 mt-1">{req.uploaded_count} file(s) already uploaded</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts Row */}
        {(d.overdue_count > 0 || d.received_pending > 0 || d.unread_notifications > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="dashboard-alerts">
            {d.overdue_count > 0 && (
              <Link to="/receivables" className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl p-4 hover:bg-red-100 transition-colors" data-testid="alert-overdue">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">{d.overdue_count} Overdue Invoice{d.overdue_count > 1 ? "s" : ""}</p>
                  <p className="text-xs text-red-600">{fmt(d.overdue_amount)} outstanding</p>
                </div>
                <ArrowRight className="w-4 h-4 text-red-400 ml-auto" />
              </Link>
            )}
            {d.received_pending > 0 && (
              <Link to="/received" className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4 hover:bg-blue-100 transition-colors" data-testid="alert-received">
                <Inbox className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">{d.received_pending} Pending Received</p>
                  <p className="text-xs text-blue-600">Awaiting review</p>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 ml-auto" />
              </Link>
            )}
            {d.unread_notifications > 0 && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4" data-testid="alert-notifications">
                <Bell className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">{d.unread_notifications} Unread Notification{d.unread_notifications > 1 ? "s" : ""}</p>
                  <p className="text-xs text-amber-600">Check your alerts</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent Invoices + Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Invoices */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="recent-invoices">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="text-base font-semibold text-[#111827]">Recent Invoices</h2>
              <Link to="/invoices" className="text-sm text-[#2563EB] hover:underline flex items-center gap-1" data-testid="view-all-invoices">
                View all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {(d.recent_invoices || []).length > 0 ? (
              <div className="divide-y divide-slate-50">
                {(d.recent_invoices || []).map((inv) => (
                  <Link
                    key={inv.invoice_id}
                    to={`/invoices/${inv.invoice_id}`}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors"
                    data-testid={`recent-inv-${inv.invoice_id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      inv.invoice_type === "sales" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"
                    }`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{inv.customer_name}</p>
                      <p className="text-xs text-slate-500">{inv.invoice_number} &middot; {formatIndianDate(inv.invoice_date)}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadge(inv.payment_status)}`}>
                      {inv.payment_status}
                    </span>
                    <span className="text-sm font-bold text-slate-900 min-w-[80px] text-right">{fmt(inv.grand_total)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400" data-testid="recent-invoices-empty">
                <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium text-slate-600 mb-0.5">No invoices yet</p>
                <p className="text-xs text-slate-400 mb-3">Create your first invoice — it takes under a minute.</p>
                <Link to="/create" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline" data-testid="recent-invoices-empty-cta">
                  <Plus className="w-3 h-3" /> Create Invoice
                </Link>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="quick-actions">
            <h2 className="text-base font-semibold text-[#111827] mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { label: "Create Invoice", icon: Plus, path: "/create", color: "bg-blue-50 text-blue-600", free: true },
                { label: "Quotations", icon: FileText, path: "/quotations", color: "bg-indigo-50 text-indigo-600", free: true },
                { label: "View Receivables", icon: CreditCard, path: "/receivables", color: "bg-violet-50 text-violet-600", free: true },
                { label: "Received Invoices", icon: Inbox, path: "/received", color: "bg-indigo-50 text-indigo-600", free: true },
                { label: "Recurring Invoices", icon: Clock, path: "/recurring", color: "bg-teal-50 text-teal-600", free: true },
                { label: "Analyze Invoice", icon: Upload, path: freeLockedCard ? "/pricing" : "/upload", color: freeLockedCard ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-600", free: false },
                { label: "GST Reports", icon: BarChart3, path: freeLockedCard ? "/pricing" : "/reports", color: freeLockedCard ? "bg-slate-100 text-slate-400" : "bg-amber-50 text-amber-600", free: false },
              ].map((a) => (
                <Link
                  key={a.label}
                  to={a.path}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                    freeLockedCard && !a.free ? "hover:bg-violet-50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${a.color} flex items-center justify-center`}>
                    <a.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-sm font-medium ${freeLockedCard && !a.free ? "text-slate-400" : "text-slate-700 group-hover:text-slate-900"}`}>{a.label}</span>
                  {freeLockedCard && !a.free ? (
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
                      <Lock className="w-2.5 h-2.5" /> Upgrade
                    </span>
                  ) : (
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-slate-500 transition-colors" />
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
